import { inject, Injectable } from '@angular/core'
import Echo from 'laravel-echo'
import { lastValueFrom } from 'rxjs'
import { EnvUtilities } from '../../core/utilities/env-utilities'
import { AnonymousData } from '../../rest/decorators/mapped'
import { FractalService } from '../../rest/services/fractal.service'
import { HttpService } from '../../rest/services/http.service'
import { BroadcastChannelVisibility } from '../enums/broadcast-channel-visibility'
import { BroadcastEvent } from '../enums/broadcast-event'
import { BROADCAST_FACTORY } from '../tokens/broadcast'
import { BroadcastChannel } from '../utilities/broadcast-channel'

@Injectable({ providedIn: 'root' })
export class BroadcastService {
  private http = inject(HttpService)
  private fractal = inject(FractalService)
  private factory = inject(BROADCAST_FACTORY)
  private echo: Echo<'reverb'>

  constructor() {
    const config = EnvUtilities.echoConfig()

    this.echo = this.factory.echo({
      broadcaster: 'reverb',
      ...config,
      client: this.factory.pusher(config.key, {
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
          customHandler: (params, callback) =>
            this.http
              .post<{ auth: string }>(
                EnvUtilities.serviceUrl('broadcasting/auth', ''),
                {
                  socket_id: params.socketId,
                  channel_name: params.channelName,
                }
              )
              .then((response) => lastValueFrom(response))
              .then((response) => {
                callback(null, response)
              })
              .catch((error) => {
                if (error) {
                  callback(error, null)
                }
              }),
          transport: 'ajax',
          endpoint: EnvUtilities.serviceUrl('broadcasting/auth', ''),
        },
        ...EnvUtilities.reverbConfig(),
      }),
    })
  }

  joinChannel(channel: BroadcastChannel) {
    switch (channel.visibility) {
      case BroadcastChannelVisibility.private:
        return this.echo.private(channel.getRoute())
      case BroadcastChannelVisibility.public:
        return this.echo.channel(channel.getRoute())
    }
  }

  getEventHandler<Model>(
    createModel: (data: AnonymousData) => Model,
    emit: (model: Model) => void
  ) {
    return (data: AnonymousData) => {
      emit(
        this.fractal.deserialiseItem<AnonymousData, Model>(data, createModel)
      )
    }
  }

  subscribeToEvent<Model>(
    channel: BroadcastChannel,
    event: BroadcastEvent,
    createModel: (data: AnonymousData) => Model,
    emit: (model: Model) => void
  ) {
    return this.joinChannel(channel).listen(
      event,
      this.getEventHandler(createModel, emit)
    )
  }

  leaveChannel(channel: BroadcastChannel) {
    this.echo.leave(channel.getRoute())
  }
}
